import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';

import type { Id } from '@e-socna/data-layer';

import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

import { EstablishmentResultRow } from './EstablishmentResultRow';
import type { EstablishmentSettledOutcome } from './hooks/useEstablishmentOrganizationCreation.hook';
import type { ActiveMembership } from './types/active-membership.type';
import type { EstablishmentListItem } from './utils/build-establishments-list.util';
import type { OrganizationSearchResult } from './types/organization-search-result.type';

export interface ArmedEstablishment {
  readonly isSelected: boolean;
  readonly item: EstablishmentListItem;
}

interface Props {
  readonly activeMemberships: readonly ActiveMembership[];
  readonly armedEstablishments: readonly ArmedEstablishment[];
  readonly onPendingChange: (hasPending: boolean) => void;
  readonly result: OrganizationSearchResult;
}

export const EstablishmentsResultsList = ({
  activeMemberships,
  armedEstablishments,
  onPendingChange,
  result,
}: Readonly<Props>) => {
  const { enqueueSnackbar } = useSnackbar();

  const armedIndices = useMemo(
    () =>
      armedEstablishments
        .map((entry, index) => ({ ...entry, index }))
        .filter((entry) => entry.isSelected),
    [armedEstablishments],
  );

  const motherIndex = useMemo(
    () => armedIndices.find((entry) => entry.item.isHeadOffice)?.index ?? -1,
    [armedIndices],
  );

  const [motherOrganizationId, setMotherOrganizationId] = useState<Id | null>(
    null,
  );
  const [settledOutcomes, setSettledOutcomes] = useState<
    ReadonlyMap<number, EstablishmentSettledOutcome>
  >(new Map());

  const handleRowSettled = useCallback(
    (index: number, outcome: Readonly<EstablishmentSettledOutcome>) => {
      if (index === motherIndex && outcome.organizationId) {
        setMotherOrganizationId(outcome.organizationId);
      }

      setSettledOutcomes((previous) => {
        const next = new Map(previous);
        next.set(index, outcome);
        return next;
      });
    },
    [motherIndex],
  );

  const hasToastedRef = useRef(false);

  useEffect(() => {
    onPendingChange(
      armedIndices.length > 0 && settledOutcomes.size < armedIndices.length,
    );
  }, [armedIndices.length, onPendingChange, settledOutcomes.size]);

  useEffect(() => {
    if (
      hasToastedRef.current ||
      armedIndices.length === 0 ||
      settledOutcomes.size < armedIndices.length
    ) {
      return;
    }

    hasToastedRef.current = true;

    const failedCount = Array.from(settledOutcomes.values()).filter(
      (outcome) =>
        outcome.status === 'rejected' || outcome.membershipsApplied === false,
    ).length;

    enqueueSnackbar(
      failedCount > 0
        ? "Au moins une organisation n'a pas été créée ou n'a pas pu être complétée (voir le détail ligne par ligne)."
        : 'Toutes les organisations sélectionnées ont été créées.',
      { variant: failedCount > 0 ? 'error' : 'success' },
    );
  }, [armedIndices.length, enqueueSnackbar, settledOutcomes]);

  return (
    <Stack divider={<Divider />} spacing={1}>
      {armedEstablishments.map((entry, index) => {
        const isWaitingForHeadOffice =
          !entry.item.isHeadOffice &&
          motherIndex !== -1 &&
          motherOrganizationId === null;

        const canStart = entry.isSelected && !isWaitingForHeadOffice;

        return (
          <EstablishmentResultRow
            activeMemberships={activeMemberships}
            canStart={canStart}
            index={index}
            isSelected={entry.isSelected}
            isWaitingForHeadOffice={isWaitingForHeadOffice}
            item={entry.item}
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            motherId={entry.item.isHeadOffice ? null : motherOrganizationId}
            onSettled={handleRowSettled}
            result={result}
          />
        );
      })}
    </Stack>
  );
};
